<template>
  <section class="form-fw__content">
    <form
      v-if="status !== isFailed"
      class="form--custom"
      novalidate
      @submit.prevent="handleSubmit"
    >
      <fieldset
        class="form__container"
        :disabled="status === isLoading"
      >
        <div class="form__container__content">
          <label>{{ $t(`${namespace}.fieldDisplayNameLabel`) }}</label>
          <v-text-field
            v-model="form.displayName"
            :placeholder="$t(`${namespace}.fieldDisplayNamePlaceholder`)"
            :error-messages="errors.collect($t(`${namespace}.fieldDisplayNameName`))"
            :data-vv-name="$t(`${namespace}.fieldDisplayNameName`)"
            required
            flat
            outline
            solo
            color="accent"
          />

          <v-layout row wrap>
            <v-flex md6>
              <div class="pr-3--desktop">
                <label>{{ $t(`${namespace}.fieldCompanyLabel`) }}*</label>
                <v-text-field
                  v-model="form.name"
                  :placeholder="$t(`${namespace}.fieldCompanyPlaceholder`)"
                  :error-messages="errors.collect($t(`${namespace}.fieldCompanyName`))"
                  v-validate="{ required: true }"
                  :data-vv-name="$t(`${namespace}.fieldCompanyName`)"
                  required
                  flat
                  outline
                  solo
                  color="accent"
                />
              </div>
            </v-flex>

            <v-flex md6>
              <div class="pl-3--desktop">
                <label>{{ $t(`${namespace}.fieldDocumentIdLabel`) }}*</label>
                <v-text-field
                  v-model="form.documentId"
                  :placeholder="$t(`${namespace}.fieldDocumentIdPlaceholder`)"
                  :error-messages="errors.collect($t(`${namespace}.fieldDocumentIdName`))"
                  v-validate="{
                    required: true,
                    cnpj: true
                  }"
                  mask="##.###.###/####-##"
                  :data-vv-name="$t(`${namespace}.fieldDocumentIdName`)"
                  required
                  flat
                  outline
                  solo
                  color="accent"
                />
              </div>
            </v-flex>
          </v-layout>

          <label>{{ $t(`${namespace}.fieldCompanyGroupLabel`) }}</label>
          <div class="form__group">
            <transition name="fade">
              <AutoComplete
                v-if="!isFetching"
                kind="companyGroup"
                :data="companyGroup"
                :placeholder="$t(`${namespace}.fieldCompanyGroupPlaceholder`)"
                :autofocus="companyGroupAutoFocus"
                @input="updateCompanyGroup"
              />
            </transition>
          </div>

          <label>{{ $t(`${namespace}.fieldContactEmailListLabel`) }}*</label>
          <v-combobox
            v-model="form.contactEmailList"
            :placeholder="$t(`${namespace}.fieldContactEmailListPlaceholder`)"
            :error-messages="errors.collect($t(`${namespace}.fieldContactEmailListName`))"
            v-validate="{ required: true, emailList: true }"
            :data-vv-name="$t(`${namespace}.fieldContactEmailListName`)"
            noDataText=""
            multiple
            chips
            required
            flat
            outline
            solo
            color="accent"
          >
            <template v-slot:selection="{ item, parent, selected }">
              <v-chip
                :selected="selected"
                :class="[
                  { 'error': !emailRegex.test(item) },
                  { 'theme--dark': !emailRegex.test(item) },
                ]"
                close
                @input="parent.selectItem(item)"
              >
                {{ item }}
              </v-chip>
            </template>
          </v-combobox>

          <v-layout row wrap>
            <v-flex md6>
              <div class="pr-3--desktop">
                <label>{{ $t(`${namespace}.fieldPostalCodeLabel`) }}*</label>
                <v-text-field
                  v-model="form.postalCode"
                  :placeholder="$t(`${namespace}.fieldPostalCodePlaceholder`)"
                  :error-messages="errors.collect($t(`${namespace}.fieldPostalCodeName`))"
                  v-validate="{ required: true, regex: /^(\d{5})-?(\d{3})$/ }"
                  maxlength="8"
                  mask="#####-###"
                  :data-vv-name="$t(`${namespace}.fieldPostalCodeName`)"
                  required
                  flat
                  outline
                  solo
                  color="accent"
                />
              </div>
            </v-flex>

            <v-flex md6>
              <div class="pl-3--desktop">
                <label>{{ $t(`${namespace}.fieldPhoneLabel`) }}*</label>
                <v-text-field
                  v-model="phone"
                  :placeholder="$t(`${namespace}.fieldPhonePlaceholder`)"
                  :error-messages="errors.collect($t(`${namespace}.fieldPhoneName`))"
                  v-validate="{ required: true, regex: /^\(\d{2}\)\s(\d{4,5})-(\d{4})$/ }"
                  :data-vv-name="$t(`${namespace}.fieldPhoneName`)"
                  required
                  flat
                  outline
                  solo
                  color="accent"
                  maxlength="15"
                  @input="setPhoneMask($event, 'phone')"
                />
              </div>
            </v-flex>
          </v-layout>

          <v-layout row wrap>
            <v-flex md6>
              <div class="pr-3--desktop">
                <label>{{ $t(`${namespace}.fieldAddressLabel`) }}*</label>
                <v-text-field
                  v-model="form.address"
                  :placeholder="$t(`${namespace}.fieldAddressPlaceholder`)"
                  :error-messages="errors.collect($t(`${namespace}.fieldAddressName`))"
                  v-validate="{
                    required: true,
                    min: 3
                  }"
                  :data-vv-name="$t(`${namespace}.fieldAddressName`)"
                  required
                  flat
                  outline
                  solo
                  color="accent"
                />
              </div>
            </v-flex>

            <v-flex md6>
              <div class="pl-3--desktop">
                <label>{{ $t(`${namespace}.fieldDistrictLabel`) }}*</label>
                <v-text-field
                  v-model="form.district"
                  :placeholder="$t(`${namespace}.fieldDistrictPlaceholder`)"
                  :error-messages="errors.collect($t(`${namespace}.fieldDistrictName`))"
                  v-validate="{ required: true }"
                  :data-vv-name="$t(`${namespace}.fieldDistrictName`)"
                  required
                  flat
                  outline
                  solo
                  color="accent"
                />
              </div>
            </v-flex>
          </v-layout>

          <v-layout row wrap>
            <v-flex md6>
              <div class="pr-3--desktop">
                <label>{{ $t(`${namespace}.fieldStateLabel`) }}*</label>
                <v-select
                  v-model="form.state"
                  :placeholder="$t(`${namespace}.fieldStatePlaceholder`)"
                  item-text="label"
                  :items="objectToArray($t('Common.statesList'))"
                  :error-messages="errors.collect($t(`${namespace}.fieldStateName`))"
                  v-validate="{ required: true }"
                  :data-vv-name="$t(`${namespace}.fieldStateName`)"
                  noDataText=""
                  required
                  flat
                  outline
                  solo
                  color="accent"
                />
              </div>
            </v-flex>

            <v-flex md6>
              <div class="pl-3--desktop">
                <label>{{ $t(`${namespace}.fieldCityLabel`) }}*</label>
                <v-text-field
                  v-model="form.city"
                  :placeholder="$t(`${namespace}.fieldCityPlaceholder`)"
                  :error-messages="errors.collect($t(`${namespace}.fieldCityName`))"
                  v-validate="{ required: true }"
                  :data-vv-name="$t(`${namespace}.fieldCityName`)"
                  required
                  flat
                  outline
                  solo
                  color="accent"
                />
              </div>
            </v-flex>
          </v-layout>
        </div>
      </fieldset>
    </form>

    <div
      v-if="status === isFailed"
      class="text-xs-center mt-5"
    >
      <span>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          href="#"
          :title="$t('Common.tryAgain')"
          class="text--link accent--text"
          @click.prevent="$emit('tryAgain')"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </span>
    </div>
  </section>
</template>

<script>
import { mapActions } from 'vuex'
import { isObject, isArray, objectToArray, goBack } from 'utils/functions'
import validator from 'utils/validator'
import { masks } from 'utils/masks'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import AutoComplete from 'components/AutoComplete'

export default {
  name: 'EmployeeForm',
  props: {
    kind: {
      type: String
    },
    data: {
      type: Object
    },
    submit: {
      type: Boolean
    },
    status: {
      type: Number
    },
    step: {
      type: Number,
      default: 1
    }
  },
  components: {
    AutoComplete
  },
  computed: {
    isLoading: () => STATUS_IS_LOADING,
    isSuccess: () => STATUS_SUCCESS,
    isFailed: () => STATUS_FAILED,
    companyGroupAutoFocus () {
      return Boolean(this.$route.query.companyGroup && this.$route.query.companyGroup === 'focus')
    }
  },
  data () {
    return {
      namespace: 'Views.CompanyDataPage.form',
      isFetching: false,
      companyGroup: null,
      isCompanyGroupValid: true,
      emailRegex: /^([a-zA-Z0-9_.-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/,
      phone: null,
      form: {
        id: null,
        displayName: null,
        name: null,
        documentId: null,
        companyGroupId: null,
        postalCode: null,
        phone: null,
        address: null,
        district: null,
        state: null,
        city: null,
        contactEmailList: []
      }
    }
  },
  methods: {
    ...mapActions({
      successAlert: 'snackbarAlert/success',
      errorAlert: 'snackbarAlert/error'
    }),

    isValidObject: (val) => isObject(val),

    isValidArray: (val) => isArray(val),

    objectToArray: (val) => objectToArray(val),

    setPhoneMask (val, mask) {
      this.phone = masks[mask](val)
      this.form.phone = val.replace(/\D/g, '')
    },

    prevPage () {
      goBack()
    },

    async getCompanyGroupById (companyGroupId) {
      try {
        const { data: res } = await this.$apiService.companyGroup.get({
          groupId: companyGroupId
        })

        if (!this.isValidArray(res.data) || !this.isValidObject(res.data[0])) {
          throw new Error(JSON.stringify(res))
        }

        this.companyGroup = res.data[0].companyGroup
      } catch (err) {
        console.log('ERROR', err)
      }
    },

    async setData () {
      if (this.kind === 'editar' && this.isValidObject(this.data)) {
        this.isFetching = true

        const {
          id,
          displayName,
          name,
          documentId,
          companyGroupId = null,
          postalCode,
          phone,
          address,
          district,
          state,
          city,
          contactEmailList = []
        } = this.data

        this.form = {
          ...this.data,
          id,
          displayName,
          name,
          documentId: documentId.replace(/\D/g, ''),
          companyGroupId,
          postalCode: postalCode.replace(/\D/g, ''),
          phone: phone.replace(/\D/g, ''),
          address,
          district,
          state,
          city,
          contactEmailList: contactEmailList.split(/\s*,\s*/)
        }

        this.setPhoneMask(this.form.phone, 'phone')

        if (companyGroupId) {
          await this.getCompanyGroupById(companyGroupId)
        }

        // WORKAROUND AUTOCOMPLETE
        setTimeout(() => { this.isFetching = false }, 200)
      }
    },

    resetFields () {
      this.phone = null
      this.companyGroup = null
      this.form = {
        id: null,
        displayName: null,
        name: null,
        documentId: null,
        companyGroupId: null,
        postalCode: null,
        phone: null,
        address: null,
        district: null,
        state: null,
        city: null,
        contactEmailList: []
      }
    },

    updateCompanyGroup (val) {
      if (this.isValidObject(val)) {
        this.companyGroup = val
        this.form.companyGroupId = val.id
        this.isCompanyGroupValid = true
        return
      }

      this.companyGroup = []
      this.form.companyGroupId = null
      this.isCompanyGroupValid = false
    },

    errorHandler (err) {
      if (err && err.response && err.response.data && this.isValidObject(err.response.data.errors)) {
        const errors = err.response.data.errors
        const keys = Object.keys(errors)

        if (this.isValidArray(keys)) {
          const key = keys[0]
          this.errorAlert(`${key}: ${errors[key][0]}`)
          return
        }

        this.kind === 'editar'
          ? this.errorAlert(this.$t(`${this.namespace}.editErrorMessage`))
          : this.errorAlert(this.$t(`${this.namespace}.createErrorMessage`))
        return
      }

      this.kind === 'editar'
        ? this.errorAlert(this.$t(`${this.namespace}.editErrorMessage`))
        : this.errorAlert(this.$t(`${this.namespace}.createErrorMessage`))
    },

    redirectToCompanyTab () {
      this.$router
        .push({
          name: 'Company',
          query: { tab: 'company' }
        })
        .catch(() => {})
    },

    dispatchHeaderErrorMessage () {
      const { errors } = this.$validator
      const errorsLength = errors.items.length > 0
      this.$emit('headerErrorMessage', errorsLength)
    },

    async create () {
      this.$emit('setCurrentStatus', this.isLoading)

      try {
        const data = { ...this.form }
        delete data.id

        const { data: res } = await this.$apiService.company.create(data)

        if (!res || !this.isValidObject(res.data) || !res.data.id) {
          throw new Error()
        }

        const companyId = res.data.id

        // change company status
        await this.$apiService.editCompanyStatus(companyId, 'enabled')

        scrollTo(0, 0)
        this.$emit('setCurrentStatus', this.isSuccess)
        this.$router
          .push({
            name: 'CreateCompany',
            params: { id: companyId }
          })
          .catch(() => {})
      } catch (err) {
        this.errorHandler(err)
        this.$emit('setCurrentStatus', this.isSuccess)
      }
    },

    async edit () {
      this.$emit('setCurrentStatus', this.isLoading)

      try {
        const { id } = this.form
        await this.$apiService.company.update(id, this.form)
        this.successAlert(this.$t(`${this.namespace}.editSuccessMessage`))
        scrollTo(0, 0)
        this.redirectToCompanyTab()
        // this.$emit('tryAgain')
      } catch (err) {
        this.errorHandler(err)
        this.$emit('setCurrentStatus', this.isSuccess)
      }
    },

    async handleSubmit () {
      try {
        const isValid = await this.$validator.validateAll()
        this.dispatchHeaderErrorMessage()

        if (isValid) {
          this.kind === 'editar'
            ? this.edit()
            : this.create()
        } else {
          const target = this.$validator.errors.items[0]
          if (target) {
            const elem = this.$el.querySelector(`[data-vv-name="${target.field}"]`)
            elem.scrollIntoView(false)
            elem.focus()
          }
        }
      } catch (err) {
        console.log('ERROR', err)
      }
    }
  },
  watch: {
    kind () {
      this.setData()
    },

    data: {
      deep: true,
      handler () {
        this.setData()
      }
    },

    submit () {
      this.handleSubmit()
    }
  },
  created () {
    this.$validator.extend('cnpj', (value) => validator.cnpj(value))

    this.$validator.extend('emailList', {
      getMessage: field => this.$t(`${this.namespace}.emailErrorMessage`),
      validate: value => {
        const validate = value.find(email => !this.emailRegex.test(email))
        return !validate
      }
    })

    this.setData()
  }
}
</script>

<style lang="scss">
.form__group {
  min-height: 74px;
}
</style>
